/**
 * @fileoverview One prospect row of the Sales Pipeline table.
 * @description Split out of SalesPipelinePage so the page's own render stays readable. The whole
 * row opens the prospect's detail view on click; the bank name stays a real button so keyboard
 * users can open it too. The checkbox and the three-dot actions menu stop their clicks from
 * reaching the row, so selecting a row or using its menu never also opens the detail view. The
 * menu is the shared TenantRowActions: View, then every action the viewer's role allows.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useTranslation } from 'react-i18next'

import { nameForRosterId } from '@/features/sales/lib/roster-lookup.utils'
import Badge from '@/shared/components/atoms/Badge'
import Checkbox from '@/shared/components/atoms/Checkbox'

import TenantRowActions from './TenantRowActions'

import type { TenantAction } from '@/features/sales/lib/tenant-access.utils'
import type { TargetWithOverdue } from '@/features/sales/mocks/targets-mock-store'
import type { SalesUserRecord, Tenant } from '@/features/sales/types/tenant.types'

/** The table's column template, shared by its header row and every PipelineRow. */
export const PIPELINE_GRID_COLUMNS =
  'grid-cols-[34px_1.9fr_1.1fr_1fr_0.8fr_0.8fr_1.1fr_1fr_1fr_1fr_1fr_0.5fr]'

/**
 * @description The target-status key for a target: completed, overdue, or still outstanding.
 * @param {TargetWithOverdue} target - The tenant's relevant target.
 * @returns {'completed' | 'overdue' | 'outstanding'} The status key.
 */
function targetStatusKey(target: TargetWithOverdue): 'completed' | 'overdue' | 'outstanding' {
  if (target.status === 'Completed') return 'completed'
  return target.overdue ? 'overdue' : 'outstanding'
}

const STATUS_BADGE_VARIANT = {
  completed: 'success',
  overdue: 'danger',
  outstanding: 'neutral',
} as const

/**
 * @description Renders the Target Status cell: a status badge, or "No target" when there's none.
 * @param {{ target: TargetWithOverdue | undefined }} props - The tenant's relevant target, if any.
 * @param {TargetWithOverdue | undefined} props.target - The target to show the status of.
 */
function TargetStatusCell({ target }: { target: TargetWithOverdue | undefined }) {
  const { t } = useTranslation()

  if (!target) {
    return <span className="text-[12.5px] text-footer">{t('sales.pipeline.noTarget')}</span>
  }

  const key = targetStatusKey(target)
  return (
    <Badge variant={STATUS_BADGE_VARIANT[key]}>
      {t(`sales.tenantDetail.target.statusValues.${key}`)}
    </Badge>
  )
}

interface PipelineRowProps {
  tenant: Tenant
  target: TargetWithOverdue | undefined
  roster: SalesUserRecord[]
  viewerUserId: string
  viewerRole: string
  selected: boolean
  onToggleSelected: () => void
  onView: () => void
  onAction: (action: TenantAction) => void
}

/**
 * @component
 * @param {PipelineRowProps} props - The row's tenant, target, viewer and handlers.
 * @param {Tenant} props.tenant - The prospect this row shows.
 * @param {TargetWithOverdue | undefined} props.target - Its relevant target, if any.
 * @param {SalesUserRecord[]} props.roster - The sales roster, for the owner's name.
 * @param {string} props.viewerUserId - The signed-in user's sales-roster id.
 * @param {string} props.viewerRole - The signed-in user's active role name.
 * @param {boolean} props.selected - Whether its checkbox is ticked.
 * @param {() => void} props.onToggleSelected - Toggles its checkbox.
 * @param {() => void} props.onView - Opens its read-only detail view.
 * @param {(action: TenantAction) => void} props.onAction - Opens one action's form.
 */
function PipelineRow({
  tenant,
  target,
  roster,
  viewerUserId,
  viewerRole,
  selected,
  onToggleSelected,
  onView,
  onAction,
}: PipelineRowProps) {
  const { t } = useTranslation()
  const isUnassigned = tenant.currentOwnerId === null

  return (
    <div
      onClick={onView}
      className={`grid ${PIPELINE_GRID_COLUMNS} cursor-pointer items-center gap-3 border-b border-row-border px-4 py-2.5 hover:bg-surface-hover sm:px-5`}
    >
      <span onClick={(event) => event.stopPropagation()} className="flex">
        <Checkbox checked={selected} onChange={onToggleSelected} aria-label={tenant.name} />
      </span>
      <div className="flex min-w-0 flex-col">
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation()
            onView()
          }}
          className="min-w-0 cursor-pointer truncate text-left text-[13.5px] font-semibold text-primary hover:underline"
        >
          {tenant.name}
        </button>
        <span className="truncate text-xs text-footer">{tenant.id}</span>
      </div>
      <span className="truncate text-[13px] text-cell">{tenant.bankType}</span>
      <span className="truncate text-[13px] text-cell">
        {tenant.city}, {tenant.state}
      </span>
      <span className="text-[13px] font-medium text-cell">{tenant.tier}</span>
      <span className="text-[13px] text-cell">{tenant.branches}</span>
      <span
        className={`truncate text-[13px] ${isUnassigned ? 'font-semibold text-danger' : 'text-cell'}`}
      >
        {tenant.currentOwnerId === null
          ? t('sales.pipeline.filters.unassigned')
          : nameForRosterId(tenant.currentOwnerId, roster)}
      </span>
      <span className="truncate text-[13px] text-cell">{target?.stage ?? '—'}</span>
      <span className="truncate text-[13px] text-cell">{target?.periodLabel ?? '—'}</span>
      <TargetStatusCell target={target} />
      <span className="text-[13px] text-cell">
        {target?.nextFollowUpAt ? target.nextFollowUpAt.slice(0, 10) : '—'}
      </span>
      <TenantRowActions
        tenant={tenant}
        target={target}
        roster={roster}
        viewerUserId={viewerUserId}
        viewerRole={viewerRole}
        onView={onView}
        onAction={onAction}
      />
    </div>
  )
}

export default PipelineRow
